import { createSlice } from "@reduxjs/toolkit";
import {
  register,
  login,
  logout,
  getCurrentUser,
  loginWithGoogle,
  completeSocialRegistration,
  adminLoginToUser,
  adminLoginWithUid,
  exitImpersonation,
} from "./thunks";
import {
  getUserType,
  setUserType,
  clearUserType,
} from "helpers/userTypeHelpers";
import { getToken, setToken, clearToken } from "helpers/tokenHelpers";

// Helper function to get impersonation state from localStorage
const getImpersonationState = () => {
  const isImpersonated = localStorage.getItem('isImpersonated') === 'true';
  let originalAdmin = null;
  try {
    const originalAdminStr = localStorage.getItem('originalAdmin');
    if (originalAdminStr) {
      originalAdmin = JSON.parse(originalAdminStr);
    }
  } catch (e) {
    console.error("Error parsing originalAdmin from localStorage:", e);
  }
  
  return {
    isImpersonated,
    originalAdmin,
  };
};

// Initial state
const initialState = {
  error: null,
  loading: false,
  userType: getUserType(),
  currentUser: null,
  token: getToken(),
  isAuthenticated: !!getToken(),
  ...getImpersonationState(),
  needsSocialRegistration: false,
  socialUserData: null,
  socialRegistrationSuccess: false,
  successMessage: null,
};

// Utility functions
const storeSession = (token, userType, additionalData = {}) => {
  setToken(token);
  setUserType(userType);
  
  if (additionalData.isImpersonated !== undefined) {
    localStorage.setItem('isImpersonated', additionalData.isImpersonated ? 'true' : 'false');
  }
  
  if (additionalData.originalAdmin) {
    localStorage.setItem('originalAdmin', JSON.stringify(additionalData.originalAdmin));
  }
};

const clearSession = () => {
  clearToken();
  clearUserType();
  localStorage.removeItem('isImpersonated');
  localStorage.removeItem('originalAdmin');
};

// Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    resetAuthState(state) {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.token = null;
      state.currentUser = null;
      state.userType = "guest";
      state.isImpersonated = false;
      state.originalAdmin = null;
      state.needsSocialRegistration = false;
      state.socialUserData = null;
      state.socialRegistrationSuccess = false;
      state.successMessage = null;
      clearSession();
    },
    updateImpersonationState(state, action) {
      const { isImpersonated, originalAdmin } = action.payload;
      state.isImpersonated = isImpersonated;
      state.originalAdmin = originalAdmin;
      localStorage.setItem('isImpersonated', isImpersonated ? 'true' : 'false');
      if (originalAdmin) {
        localStorage.setItem('originalAdmin', JSON.stringify(originalAdmin));
      } else {
        localStorage.removeItem('originalAdmin');
      }
    },
    refreshAuthState(state) {
      const token = getToken();
      const userType = getUserType();
      const isImpersonated = localStorage.getItem('isImpersonated') === 'true';
      let originalAdmin = null;
      
      try {
        const originalAdminStr = localStorage.getItem('originalAdmin');
        if (originalAdminStr) {
          originalAdmin = JSON.parse(originalAdminStr);
        }
      } catch (e) {
        console.error("Error parsing originalAdmin:", e);
      }
      
      state.token = token;
      state.userType = userType;
      state.isAuthenticated = !!token;
      state.isImpersonated = isImpersonated;
      state.originalAdmin = originalAdmin;
    },
    setCurrentUser(state, action) {
      state.currentUser = action.payload;
      if (action.payload) {
        state.userType = action.payload.type;
        setUserType(action.payload.type);
      }
    },
    clearSocialRegistration(state) {
      state.needsSocialRegistration = false;
      state.socialUserData = null;
      state.socialRegistrationSuccess = false;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        const { token, user } = action.payload.data;
        state.token = token;
        state.userType = user.type;
        state.currentUser = user;
        state.isAuthenticated = true;
        state.isImpersonated = user.isImpersonated || false;
        state.originalAdmin = user.originalAdmin || null;
        
        storeSession(token, user.type, {
          isImpersonated: state.isImpersonated,
          originalAdmin: state.originalAdmin
        });
        
        console.log("[AUTH SLICE] Login successful:", {
          isAuthenticated: state.isAuthenticated,
          user: state.currentUser,
          isImpersonated: state.isImpersonated
        });
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
        state.isAuthenticated = false;
      })
      
      // Admin Login with UID
      .addCase(adminLoginWithUid.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(adminLoginWithUid.fulfilled, (state, action) => {
        state.loading = false;
        const { token, user } = action.payload.data;
        state.token = token;
        state.userType = user.type;
        state.currentUser = user;
        state.isAuthenticated = true;
        state.isImpersonated = user.isImpersonated || false;
        state.originalAdmin = user.originalAdmin || null;
        
        storeSession(token, user.type, {
          isImpersonated: state.isImpersonated,
          originalAdmin: state.originalAdmin
        });
        
        console.log("[AUTH SLICE] AdminLoginWithUid successful");
      })
      .addCase(adminLoginWithUid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Admin login failed";
        state.isAuthenticated = false;
        state.isImpersonated = false;
        state.originalAdmin = null;
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Registration failed";
      })
      
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.userType = action.payload.type;
        
        if (action.payload.isImpersonated) {
          state.isImpersonated = true;
          localStorage.setItem('isImpersonated', 'true');
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        const isTokenExpired = action.payload?.some?.(
          (err) => err.code === "token_expired"
        );

        if (isTokenExpired) {
          state.token = null;
          state.userType = "guest";
          state.isAuthenticated = false;
          state.currentUser = null;
          state.isImpersonated = false;
          state.originalAdmin = null;
          clearSession();
        } else {
          state.error = action.payload;
        }
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.currentUser = null;
        state.token = null;
        state.userType = "guest";
        state.isAuthenticated = false;
        state.isImpersonated = false;
        state.originalAdmin = null;
        state.needsSocialRegistration = false;
        state.socialUserData = null;
        state.socialRegistrationSuccess = false;
        state.successMessage = null;
        clearSession();
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Logout failed";
      })
      
      // Google login
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.needsSocialRegistration = false;
        state.socialUserData = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        
        // Check if the response indicates a needsRegistration flag
        if (action.payload.needsRegistration) {
          state.needsSocialRegistration = true;
          state.socialUserData = action.payload.userData;
        } else if (action.payload.data?.token) {
          // User exists and has token, complete login
          const { token, user } = action.payload.data;
          state.token = token;
          state.currentUser = user;
          state.userType = user.type;
          state.isAuthenticated = true;
          storeSession(token, user.type);
        } else if (action.payload.user) {
          // User exists but no token (backend doesn't return token)
          // Store user data but don't set authenticated
          state.currentUser = action.payload.user;
          state.userType = action.payload.user.type;
          state.isAuthenticated = false;
          state.needsSocialRegistration = false;
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        // Check if this is a needsRegistration case
        if (action.payload?.needsRegistration) {
          state.needsSocialRegistration = true;
          state.socialUserData = action.payload.userData;
          state.error = null;
        } else {
          state.error = action.payload;
          state.needsSocialRegistration = false;
          state.socialUserData = null;
        }
      })
      
      // Complete Social Registration
      .addCase(completeSocialRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.socialRegistrationSuccess = false;
        state.successMessage = null;
      })
      .addCase(completeSocialRegistration.fulfilled, (state, action) => {
        state.loading = false;
        state.socialRegistrationSuccess = true;
        
        // Check if auto-login happened
        if (action.payload?.autoLoggedIn && action.payload?.data?.token) {
          // User is auto-logged in
          const { token, user } = action.payload.data;
          state.token = token;
          state.currentUser = user;
          state.userType = user.type;
          state.isAuthenticated = true;
          state.needsSocialRegistration = false;
          state.socialUserData = null;
          state.successMessage = action.payload.message;
          
          console.log("[AUTH SLICE] Auto-login successful, user is authenticated");
        } else if (action.payload?.data?.token) {
          // Regular login with token
          const { token, user } = action.payload.data;
          state.token = token;
          state.currentUser = user;
          state.userType = user.type;
          state.isAuthenticated = true;
          state.needsSocialRegistration = false;
          state.socialUserData = null;
          state.successMessage = action.payload.message;
          
          console.log("[AUTH SLICE] Login successful with token");
        } else {
          // No token - registration only
          state.isAuthenticated = false;
          state.token = null;
          state.needsSocialRegistration = false;
          state.socialUserData = null;
          state.successMessage = action.payload?.message || "Registration successful! Please login.";
          
          console.log("[AUTH SLICE] Registration successful, but not auto-logged in");
        }
      })
      .addCase(completeSocialRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.socialRegistrationSuccess = false;
        state.needsSocialRegistration = false;
        state.isAuthenticated = false;
        state.successMessage = null;
        
        console.log("[AUTH SLICE] Social registration rejected:", action.payload);
      })
      
      // Admin Login to User (Impersonation)
      .addCase(adminLoginToUser.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(adminLoginToUser.fulfilled, (state, action) => {
        state.loading = false;
        const { token, user, originalAdmin } = action.payload.data;
        state.token = token;
        state.userType = user.type;
        state.currentUser = user;
        state.isAuthenticated = true;
        state.isImpersonated = true;
        state.originalAdmin = originalAdmin;
        
        storeSession(token, user.type, {
          isImpersonated: true,
          originalAdmin: originalAdmin
        });
      })
      .addCase(adminLoginToUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Admin login failed";
        state.isImpersonated = false;
      })
      
      // Exit Impersonation
      .addCase(exitImpersonation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exitImpersonation.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const { token, user } = action.payload.data;
          state.token = token;
          state.userType = user.type;
          state.currentUser = user;
          state.isAuthenticated = true;
          state.isImpersonated = false;
          state.originalAdmin = null;
          storeSession(token, user.type, {
            isImpersonated: false,
            originalAdmin: null
          });
        }
      })
      .addCase(exitImpersonation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to exit impersonation";
      });
  },
});

export const { 
  clearError, 
  resetAuthState, 
  updateImpersonationState,
  refreshAuthState,
  setCurrentUser,
  clearSocialRegistration
} = authSlice.actions;
export default authSlice.reducer;